import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

export const shortcutGroups = [
  {
    title: "Navigation",
    shortcuts: [
      { keys: ["⌘", "K"], desc: "Focus search bar" },
      { keys: ["?"], desc: "Show keyboard shortcuts" },
      { keys: ["←", "→"], desc: "Switch sub-tabs on tool pages" },
      { keys: ["Esc"], desc: "Close dialogs & overlays" },
    ],
  },
  {
    title: "Home Screen",
    shortcuts: [
      { keys: ["⌘", "K"], desc: "Jump to search from anywhere" },
      { keys: ["/"], desc: "Focus search bar (Home only)" },
    ],
  },
  {
    title: "Tool Pages",
    shortcuts: [
      { keys: ["←"], desc: "Previous sub-tab" },
      { keys: ["→"], desc: "Next sub-tab" },
    ],
  },
];

const Kbd = ({ children }: { children: string }) => (
  <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-md bg-secondary border border-border text-[11px] font-mono font-semibold text-foreground shadow-[0_1px_0_1px_hsl(var(--border))]">
    {children}
  </kbd>
);

const KeyboardShortcutsOverlay = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only trigger on '?' without modifier keys, and not in inputs
      if (
        e.key === "?" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !(e.target instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm sm:max-w-md" aria-describedby="shortcuts-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Keyboard className="w-4 h-4 text-primary" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>
        <p id="shortcuts-desc" className="sr-only">List of available keyboard shortcuts</p>
        <div className="space-y-5 py-1 max-h-[60vh] overflow-y-auto">
          {shortcutGroups.map((group) => (
            <div key={group.title}>
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {group.title}
              </h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <span className="text-sm text-foreground">{shortcut.desc}</span>
                    <div className="flex items-center gap-1 shrink-0 ml-4">
                      {shortcut.keys.map((key, j) => (
                        <span key={j} className="flex items-center gap-0.5">
                          <Kbd>{key}</Kbd>
                          {j < shortcut.keys.length - 1 && (
                            <span className="text-[10px] text-muted-foreground mx-0.5">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground text-center pt-2 border-t border-border">
          Press <Kbd>?</Kbd> anytime to show this guide
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsOverlay;
